/* eslint-disable consistent-return */

/**
 * @file 全仓杠杆数据模块
 * @date 2022-03-23
 */

import { ls } from '@sfe/bone-cache';
import { i18n } from '@/locales/i18n';
import { leverageApi } from '@/apis/spot';
import { leverageApi as unifyLeverageApi } from '@/apis/unified';
import { success as successToast, error as errorToast } from '@/utils/toast';
import { useOutstandingSub } from '@/hooks/leverage';

/**
 * 是否获取过全仓杠杆风险提示
 */

let isPullLeverageRiskStatusInfo = false;

const state = {
    /**
     * 全仓杠杆 状态
     */
    isLeverage: false, // 是否全仓杠杆
    isVisible: false, // 是否显示 全仓杠杆风险开通 弹层
    isConfirmOpen: false, // 风险提示是否确认
    isPassTest: false, // 风险测试题是否通过
    questions: [], // 风险测试题列表
    isTurnOn: null, // 是否开启全仓杠杆
    closed: true, // 全仓杠杆 是否关闭成功
    isFirstOpen: false, // 记录首次开通状态
    loanCommission: ls.get('SPOT_loan_sub_active') || 'outstanding', // 借还记录 子tab选中
    isUserCheckLeverage: ls.get('SPOT_ORDER_TAB') === 'leverage', // 用户是否选中杠杆tab
    outstandingSub: useOutstandingSub(), // 待还记录 子选项
    // 配置杠杆倍数弹窗 显示/隐藏
    config: {
        visible: false,
    },
    curLeverage: '', // 升级完统一账户后，用户当前的自定义杠杆倍数
    rangeLeverage: [], // 升级完统一账户后，用户自定义倍数的可选范围
};

const errorCodeToastHandler = (res = {}) => {
    const { code } = res;

    /**
         * 统一账户升级后
         * 340098 请求参数错误
         * 340099 系统异常
         * 3400201 协议参数请求不合法
         * 3400202 风险提示不允许重复提交
         * 3400203 风险提示未提交
         * 3400204 问题问卷不允许重复提交
         * 3400205 问题问卷未提交
         * 3400206 杠杆协议未开通
         * 以上code码统一返回 10016，系统错误提示码
         *
         * 3200905 设置现货杠杆失败，有风险溢出风险
         * 3200906 强平中，调整杠杆开关失败
         * 3400207 清算中，关闭杠杆开关失败
         * 3200907 非统一交易用户，操作失败
         * 3200908 现货杠杆为关闭状态，不允许当前操作
         * 3200903 借贷中，不允许当前操作
         * 3200910 存在现货杠杆订单，调整杠杆开关失败
         * 3200300 可用余额不足, 操作被拦截
         */
    if (code === 3200300) {
        errorToast(i18n.global.t('leverageLoanTips'));
        return;
    }
    if (code === 3200903) {
        errorToast(i18n.global.t('leverageCloseErrInfo'));
        return;
    }
    if (code === 3200905) {
        errorToast(i18n.global.t('leverageLoanTips'));
        return;
    }
    if (code === 3200906 || code === 3400207) {
        errorToast(i18n.global.t('liquidationErrorInfo'));
        return;
    }
    if (code === 3200908) {
        errorToast(i18n.global.t('leverage_off_trade_tip'));
        return;
    }
    if (code === 3200910) {
        errorToast(i18n.global.t('leverageCloseTips'));
        return;
    }
    if (code === 3200311) {
        errorToast(i18n.global.t('isolatedModeNotSupportLeverageTurnOnTip'));
        return;
    }
    errorToast(i18n.global.t('leverageTryAgain'));
};

const getters = {
    isSelectedLeverage(state) {
        return state.isLeverage && state.isUserCheckLeverage;
    },
};

const mutations = {
    // 更新 全仓杠杆 信息
    UPDATE_IS_LEVERAGE(state, isLeverage) {
        state.isLeverage = isLeverage;
    },
    UPDATE_LEVERAGE_LAYER(state, visible) {
        state.isVisible = visible;
    },
    UPDATE_LEVERAGE_RISK(state, isConfirmOpen) {
        state.isConfirmOpen = isConfirmOpen;
    },
    UPDATE_LEVERAGE_QUESTION_STATE(state, isPassTest) {
        state.isPassTest = isPassTest;
    },
    UPDATE_LEVERAGE_QUESTIONS(state, questions) {
        state.questions = questions;
    },
    UPDATE_LEVERAGE_TURN(state, isTurnOn) {
        state.isTurnOn = isTurnOn;
    },
    UPDATE_LEVERAGE_CLOSED(state, closed) {
        state.closed = closed;
    },
    UPDATE_LEVERAGE_FIRST_OPEN(state, payload) {
        state.isFirstOpen = payload;
    },
    UPDATE_LOAN_COMMISSION(state, loanCommission) {
        ls.set('SPOT_loan_sub_active', loanCommission);
        state.loanCommission = loanCommission;
    },
    UPDATE_OUTSTANDING_SUB(state, outstandingSub) {
        ls.set('SPOT_outstanding_sub_active', outstandingSub);
        state.outstandingSub = outstandingSub;
    },
    UPDATE_USER_CHECK_LEVERAGE(state, isUserCheckLeverage) {
        state.isUserCheckLeverage = isUserCheckLeverage;
    },
    // 配置窗口显示/隐藏
    UPDATE_LEVERAGE_CONFIG(state, payload) {
        Object.assign(state.config, payload);
    },
    UPDATE_USER_CURRENT_LEVERAGE(state, curLeverage) {
        state.curLeverage = curLeverage;
    },
    UPDATE_RANGE_LEVERAGE(state, range) {
        state.rangeLeverage = range;
    },
};

const actions = {

    // 获取 全仓杠杆 开通提示状态
    async GET_LEVERAGE_INFO({
        commit, dispatch, rootState, rootGetters,
    }, showLayer = false) {
        const symbol = rootGetters['settings/symbolMap'][rootState.exchange.symbolId];
        // 获取是否已升级统一账户的状态
        const isUTA = rootGetters['user/isUTA'];
        // 没有交易对忽略
        if (!symbol) return;

        // 是否确认开通
        const {
            isConfirmOpen, isPassTest, isTurnOn, isUserCheckLeverage, curLeverage, rangeLeverage,
        } = state;
        if (isConfirmOpen && isPassTest) return;
        if (isConfirmOpen && !isPassTest && showLayer) {
            dispatch('GET_LEVERAGE_QUESTION');
            return;
        }

        let res = {
            success: true,
            result: {
                riskInfoStatus: isConfirmOpen,
                questionaireStatus: isPassTest,
                openStatus: isTurnOn,
                leverage: curLeverage,
                supportLeverage: rangeLeverage,
            },
        };
        if (!isPullLeverageRiskStatusInfo) {
            if (isUTA) {
                // 升级统一账户后
                res = await unifyLeverageApi.getRiskStatus();
            } else {
                // 未升级前
                res = await leverageApi.getRiskStatus();
            }
            isPullLeverageRiskStatusInfo = true;
        }
        const { result = {}, success } = res;
        if (success) {
            // 升级统一账户后的结构有变化
            let { riskInfoStatus, questionaireStatus, openStatus } = result || {};
            if (isUTA) {
                // 升级统一账户后，由于字段类型有所不同（这个接口也会在资产页使用，之前的INT类型不满足，所以字段类型和之前不太一样），需要单独设置下；
                riskInfoStatus = riskInfoStatus === 'Y';
                questionaireStatus = questionaireStatus === 'Y';
                openStatus = openStatus === 'Y';

                const { leverage, supportLeverage } = result || {};
                commit('UPDATE_USER_CURRENT_LEVERAGE', leverage);
                commit('UPDATE_RANGE_LEVERAGE', supportLeverage);
            } else {
                riskInfoStatus = riskInfoStatus === 1;
                questionaireStatus = questionaireStatus === 1;
                openStatus = openStatus === 1;
            }
            commit('UPDATE_LEVERAGE_RISK', riskInfoStatus);
            commit('UPDATE_LEVERAGE_TURN', openStatus);

            // 检查是否通过考试
            if (questionaireStatus) {
                commit('UPDATE_LEVERAGE_QUESTION_STATE', questionaireStatus);
                return;
            }
            if (showLayer) {
                if (riskInfoStatus && !questionaireStatus) {
                    dispatch('GET_LEVERAGE_QUESTION');
                    return;
                }
                commit('UPDATE_LEVERAGE_LAYER', isUserCheckLeverage);
            }
            return;
        }
        errorToast(i18n.global.t('leverageTryAgain'));
    },
    // 获取 全仓杠杆 测试题
    async GET_LEVERAGE_QUESTION({ commit }) {
        if (state.questions.length) {
            commit('UPDATE_LEVERAGE_LAYER', state.isUserCheckLeverage);
            return;
        }
        const res = await leverageApi.getQuestion({ type: 2 }) || {};
        const { success, result = [] } = res;
        if (success && result.length) {
            commit('UPDATE_LEVERAGE_QUESTIONS', result);
            commit('UPDATE_LEVERAGE_LAYER', state.isUserCheckLeverage);
            return Promise.resolve();
        }
    },
    // 开启/关闭全仓杠杆
    async OPEN_CLOSE_LEVERAGE({ rootGetters, commit }, status = 0) {
        // 获取是否已升级统一账户的状态
        const isUTA = rootGetters['user/isUTA'];
        let res = null;
        if (isUTA) {
            // 开启时，调用接口
            if (status === 1) {
                res = await unifyLeverageApi.submitQuestion({ openStatus: 'Y' });
                if (res.success) {
                    commit('UPDATE_LEVERAGE_TURN', true);
                    return;
                }
                errorCodeToastHandler(res);
                return;
            }
            // 关闭时，先弹出警告提示层（设置关闭弹层为false，弹层就会弹出来）
            commit('UPDATE_LEVERAGE_CLOSED', false);
            return;
        }

        res = await leverageApi.submitQuestion({ type: 3, status });

        if (res.success) {
            commit('UPDATE_LEVERAGE_TURN', !!status);
            commit('UPDATE_LEVERAGE_CLOSED', true);
            return;
        }
        const { code, message } = res;
        /**
         * 统一账户升级前
         * 100201 场外借贷用户
         * 100215 爆仓
         * 100216 用户已穿仓
         * 100217 当前现货账户有未还欠款
         * 100502 当前账户有止盈止损订单
         */
        if (code === 100201) {
            errorToast(i18n.global.t('otcErrorInfo'));
            return;
        }
        if (code === 100215) {
            errorToast(i18n.global.t('liquidationErrorInfo'));
            return;
        }
        if (code === 100216 || code === 100217) {
            commit('UPDATE_LEVERAGE_CLOSED', false);
            return;
        }
        // 调用公共提示组件
        if (code === 100502) {
            commit('exchange/UPDATE_RISK', {
                visible: true,
                content: i18n.global.t('stop_limit_tips_2'),
            }, { root: true });
            return;
        }
        errorToast(message);
    },
    // 升级统一账户后，强制关闭全仓杠杆
    async FORCE_CLOSE_LEVERAGE({ commit }) {
        const res = await unifyLeverageApi.submitQuestion({ openStatus: 'N' });
        commit('UPDATE_LEVERAGE_CLOSED', true);
        if (res.success) {
            commit('UPDATE_LEVERAGE_TURN', false);
            return;
        }
        errorCodeToastHandler(res);
    },
    // 升级统一账户后，修改杠杆位数
    async UPDATE_LEVERAGE_CONFIG_DATA({ commit }, leverage) {
        const res = await unifyLeverageApi.submitQuestion({ leverage });
        const { code, success } = res;
        if (!(code === 0 && success)) {
            errorCodeToastHandler(res);
            return;
        }
        successToast(i18n.global.t('operationSuccess'));
        commit('UPDATE_USER_CURRENT_LEVERAGE', leverage); // 更新tab、按钮与弹窗默三处位数显示
        commit('UPDATE_LEVERAGE_CONFIG', { visible: false }); // 关闭弹窗
    },
    // UAT 用户切换保证金模式后，重新获取当前杠杆的开通状态
    async GET_LEVERAGE_INFO_WITH_UTA({ commit }) {
        const res = await unifyLeverageApi.getRiskStatus();
        const { success, result = {} } = res;
        if (success) {
            const { openStatus, questionaireStatus } = result;
            commit('UPDATE_LEVERAGE_TURN', openStatus === 'Y');
            commit('UPDATE_LEVERAGE_QUESTION_STATE', questionaireStatus === 'Y');
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
